import { base64ToArrayBuffer } from '@/helpers';
import { depositSignService } from '@/services/deposit-sign.service';
import { ISignStrategy } from '@/sign/strategies/isign.strategy';
import { determineHashAlgorithm } from '../helpers/sign.helper';
import { SignPrivateKeyStrategy } from '../strategies/sign-private-key.strategy';
import { getRecaptchaTokenIfNotAuthenticated } from '@/helpers/recaptha-helper';
import { FileHashingResult, FileSigningResult } from '@/models/document-sign.models';
import { utilityService } from '@/services/utility.service';

type Constructor<T> = new (...args: any[]) => T;

export function MixWithOwnCertFromSigningParams<T extends Constructor<ISignStrategy>>(Base: T) {
  return class extends Base {
    protected async getOwnCertificates(): Promise<any> {
      return this.signingParams.data.ownCertificates;
    }
  };
}

export function MixWithDepositSignLoginPswDocumentSigning<T extends Constructor<SignPrivateKeyStrategy>>(Base: T) {
  return class extends MixWithOwnCertFromSigningParams(Base) {
    protected async isKeyPrivateKeyReaded(): Promise<boolean> {
      return true;
    }

    protected async isStampPrivateKeyReaded(): Promise<boolean> {
      return true;
    }

    protected async signKeyTypeHashes(keyType: number, hashes: Array<FileHashingResult>, lib: any): Promise<Array<FileSigningResult>> {
      const hashAlgo = determineHashAlgorithm(this.keyType);
      for (const hash of hashes) {
        if (hash.isInternalNested) {
          let signContent = base64ToArrayBuffer(hash.sign);
          hash.hasheBase64 = await this.euSignLib.HashData(hashAlgo, signContent, true);
        }
      }

      const { depositSignUserSessionId, keyId, keyPassword } = this.signingParams.data;

      const mappedHashes = hashes.map(h => ({ id: h.pDocumentVersionsFileCode, data: h.hasheBase64 }));

      let { recaptchaToken, recaptchaType } = await getRecaptchaTokenIfNotAuthenticated();

      const signedHashes = await depositSignService.signHashes(depositSignUserSessionId, keyId, keyPassword, mappedHashes,
        recaptchaToken, recaptchaType);

      let signResults: FileSigningResult[] = [];
      for (let signedHash of signedHashes) {
        let hash = hashes.find(h => h.pDocumentVersionsFileCode === signedHash.id)!;
        let signResult = new FileSigningResult({
          pDocumentVersionsFileCode: hash.pDocumentVersionsFileCode,
          pSignBase64: null,
          isInternalNested: hash.isInternalNested,
          documentVerFileSignDataId: hash.documentVerFileSignDataId
        });

        if (hash.isInternalNested) {
          let { recaptchaToken, recaptchaType } = await getRecaptchaTokenIfNotAuthenticated();

          let creationResult = await utilityService.createInternalNestedSign(hash.sign, signedHash.sign, recaptchaToken, recaptchaType);
          if (!creationResult || !creationResult.newSignature) {
            throw new Error('Failed to create signature');
          }
          signResult.pSignBase64 = creationResult.newSignature;
        } else {
          signResult.pSignBase64 = signedHash.sign;
        }

        signResults.push(signResult);
      }

      return signResults;
    }
  };
}
